/**
 * Optional mobile entrances. No scroll listener, timers, rAF loop or React updates.
 * Baseline HTML is always visible; only entering, below-fold elements animate.
 */
export type MobileRevealKind = "copy" | "media";

type Entry = { element: HTMLElement; finish: () => void; cleanup: () => void };

export function createMobileRevealController(win: Window, doc: Document) {
  const mobile = win.matchMedia("(max-width: 767px)");
  const reduced = win.matchMedia("(prefers-reduced-motion: reduce)");
  const entries = new Set<Entry>();
  const waiting = new Map<Element, Entry>();
  let observer: IntersectionObserver | null = null;

  const allowed = () => mobile.matches && !reduced.matches && !doc.hidden;
  const targetsHash = (el: HTMLElement) => {
    const target = doc.querySelector(":target");
    return !!target && (el === target || el.contains(target) || target.contains(el));
  };
  const disconnectIfIdle = () => {
    if (!waiting.size) { observer?.disconnect(); observer = null; }
  };
  const finishAll = () => { for (const entry of entries) entry.finish(); };
  const onVisibility = () => { if (doc.hidden) finishAll(); };
  const onPageShow = (event: PageTransitionEvent) => { if (event.persisted) finishAll(); };
  const listen = () => {
    mobile.addEventListener("change", finishAll);
    reduced.addEventListener("change", finishAll);
    doc.addEventListener("visibilitychange", onVisibility);
    win.addEventListener("hashchange", finishAll);
    win.addEventListener("pageshow", onPageShow);
  };
  const unlisten = () => {
    mobile.removeEventListener("change", finishAll);
    reduced.removeEventListener("change", finishAll);
    doc.removeEventListener("visibilitychange", onVisibility);
    win.removeEventListener("hashchange", finishAll);
    win.removeEventListener("pageshow", onPageShow);
  };

  return {
    register(element: HTMLElement, kind: MobileRevealKind) {
      // Do not initialize effects for desktop, reduced-motion or missing APIs.
      if (!allowed() || typeof IntersectionObserver === "undefined") return () => {};
      const rect = element.getBoundingClientRect();
      // Entire initial viewport stays untouched, including its bottom 10%.
      if (rect.top <= win.innerHeight || element.contains(doc.activeElement) || targetsHash(element)) return () => {};

      let disposed = false;
      const finish = () => {
        element.dataset.twMotionState = "done";
        observer?.unobserve(element);
        waiting.delete(element);
        disconnectIfIdle();
      };
      const onAnimationEnd = (event: AnimationEvent) => {
        if (event.target === element && event.animationName.startsWith("tw-mobile-")) finish();
      };
      const entry: Entry = {
        element, finish,
        cleanup: () => {
          if (disposed) return;
          disposed = true;
          finish();
          element.removeEventListener("animationend", onAnimationEnd);
          element.removeEventListener("focusin", finish);
          delete element.dataset.twMotionState;
          delete element.dataset.twMotionKind;
          entries.delete(entry);
          if (!entries.size) unlisten();
        },
      };
      if (!entries.size) listen();
      entries.add(entry);
      element.dataset.twMotionKind = kind;
      element.dataset.twMotionState = "waiting"; // No CSS hides this state.
      element.addEventListener("animationend", onAnimationEnd);
      element.addEventListener("focusin", finish);
      waiting.set(element, entry);
      try {
        observer ??= new IntersectionObserver((changes) => {
          for (const change of changes) {
            const current = waiting.get(change.target);
            if (!current) continue;
            if (!allowed() || change.boundingClientRect.bottom <= 0 ||
                current.element.contains(doc.activeElement) || targetsHash(current.element)) {
              current.finish();
            } else if (change.isIntersecting) {
              current.element.dataset.twMotionState = "enter";
              observer?.unobserve(change.target);
              waiting.delete(change.target);
            }
          }
          disconnectIfIdle();
        }, { threshold: 0, rootMargin: "0px 0px -88px 0px" });
        observer.observe(element);
      } catch {
        // An unavailable/failed observer must never compromise visible content.
        entry.cleanup();
      }
      return entry.cleanup;
    },
  };
}

let controller: ReturnType<typeof createMobileRevealController> | undefined;

export function registerMobileScrollReveal(element: HTMLElement, kind: MobileRevealKind) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => {};
  controller ??= createMobileRevealController(window, document);
  return controller.register(element, kind);
}

